import { getApiUrl, getAuthHeaders, handleApiResponse } from '../config/api';

// Interfaces
export interface Blog {
  id: number;
  title: string;
  slug: string;
  bannerImage?: string;
  content: string;
  description?: string;
  publisherName: string;
  category?: string;
  tags?: string[];
  viewCount: number;
  commentCount: number;
  sortOrder: number;
  isActive: boolean;
  isFeatured: boolean;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  comments?: BlogComment[];
}

export interface BlogComment {
  id: number;
  blogId: number;
  name: string;
  email: string;
  comment: string;
  isApproved: boolean;
  parentId?: number;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  updatedAt: string;
  blog?: {
    id: number;
    title: string;
    slug: string;
  };
}

export interface CreateBlogData {
  title: string;
  slug: string;
  bannerImage?: string;
  content: string;
  description?: string;
  publisherName: string;
  category?: string;
  tags?: string[];
  sortOrder?: number;
  isActive?: boolean;
  isFeatured?: boolean;
  publishedAt?: string;
}

export interface UpdateBlogData extends Partial<CreateBlogData> {
  id: number;
}

export interface BlogFilters {
  category?: string;
  isActive?: boolean;
  isFeatured?: boolean;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface BlogListResponse {
  success: boolean;
  message: string;
  data: {
    blogs: Blog[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
    };
  };
}

export interface BlogResponse {
  success: boolean;
  message: string;
  data?: {
    blog: Blog;
  };
}

export interface BlogStats {
  totalBlogs: number;
  activeBlogs: number;
  featuredBlogs: number;
  totalViews: number;
  totalComments: number;
  categoryStats: Array<{
    category: string;
    count: number;
  }>;
}

export interface CreateBlogCommentData {
  blogId: number;
  name: string;
  email: string;
  comment: string;
  parentId?: number;
}

export interface BlogCommentFilters {
  blogId?: number;
  isApproved?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface BlogCommentListResponse {
  success: boolean;
  message: string;
  data: {
    comments: BlogComment[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
    };
  };
}

export interface BlogCommentResponse {
  success: boolean;
  message: string;
  data?: {
    comment: BlogComment;
  };
}

class BlogService {
  private baseUrl = '/admin/blogs';

  // Get all blogs with filters
  async getBlogs(filters: BlogFilters = {}): Promise<BlogListResponse> {
    try {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await fetch(getApiUrl(`${this.baseUrl}?${params.toString()}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch blogs');
    }
  }

  // Get blog by ID
  async getBlogById(id: number): Promise<BlogResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/${id}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch blog');
    }
  }

  // Get blog by slug
  async getBlogBySlug(slug: string): Promise<BlogResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/slug/${slug}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch blog');
    }
  }

  // Create blog
  async createBlog(blogData: CreateBlogData, imageFile?: File): Promise<BlogResponse> {
    try {
      const formData = new FormData();
      
      // Append blog data
      Object.entries(blogData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value.toString());
          }
        }
      });

      // Append image file if provided
      if (imageFile) {
        formData.append('bannerImage', imageFile);
      }

      const token = localStorage.getItem('admin_token');
      const response = await fetch(getApiUrl(this.baseUrl), {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to create blog');
    }
  }

  // Update blog
  async updateBlog(id: number, blogData: Partial<CreateBlogData>, imageFile?: File): Promise<BlogResponse> {
    try {
      const formData = new FormData();
      
      // Append blog data
      Object.entries(blogData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value.toString());
          }
        }
      });

      // Append image file if provided
      if (imageFile) {
        formData.append('bannerImage', imageFile);
      }

      const token = localStorage.getItem('admin_token');
      const response = await fetch(getApiUrl(`${this.baseUrl}/${id}`), {
        method: 'PUT',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update blog');
    }
  }

  // Toggle blog status
  async toggleBlogStatus(id: number, isActive: boolean): Promise<BlogResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/${id}/toggle-status`), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ isActive }),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to toggle blog status');
    }
  }

  // Delete blog
  async deleteBlog(id: number): Promise<BlogResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to delete blog');
    }
  }

  // Get featured blogs
  async getFeaturedBlogs(limit: number = 6): Promise<BlogListResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/featured?limit=${limit}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch featured blogs');
    }
  }

  // Get blogs by category
  async getBlogsByCategory(category: string, limit: number = 10): Promise<BlogListResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/category/${category}?limit=${limit}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch blogs by category');
    }
  }

  // Search blogs
  async searchBlogs(query: string, limit: number = 10): Promise<BlogListResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/search?q=${encodeURIComponent(query)}&limit=${limit}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to search blogs');
    }
  }

  // Get blog statistics
  async getBlogStats(): Promise<{ success: boolean; message: string; data?: BlogStats }> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/stats`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch blog statistics');
    }
  }

  // Blog Comment methods
  // Create blog comment
  async createBlogComment(commentData: CreateBlogCommentData): Promise<BlogCommentResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/comments`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(commentData),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to create comment');
    }
  }

  // Get blog comments
  async getBlogComments(filters: BlogCommentFilters = {}): Promise<BlogCommentListResponse> {
    try {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await fetch(getApiUrl(`${this.baseUrl}/comments?${params.toString()}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch blog comments');
    }
  }

  // Toggle comment approval status
  async toggleCommentStatus(id: number, isApproved: boolean): Promise<BlogCommentResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/comments/${id}/toggle-status`), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ isApproved }),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to toggle comment status');
    }
  }

  // Delete blog comment
  async deleteBlogComment(id: number): Promise<BlogCommentResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/comments/${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to delete comment');
    }
  }
}

export const blogService = new BlogService();
